export interface FieldDiff {
  field: string;
  device: unknown;
  cloud: unknown;
}

export interface EntityCompare {
  status?: string;
  deviceCount?: number;
  cloudCount?: number;
  differences?: Array<{ id: string; fields: FieldDiff[] }>;
  missingInCloud?: string[];
  missingOnDevice?: string[];
}

export interface DiffRow {
  id: string;
  kind: "missingOnDevice" | "missingInCloud" | "changed";
  field?: string;
  device?: unknown;
  cloud?: unknown;
}

export const IGNORED_SYNC_COMPARE_FIELDS = new Set([
  "isRecentlyDiscovered",
  "isUserSelected",
  "macAddress",
  "lastUpdated"
]);

export function isIgnorableTimestampField(field?: string | null): boolean {
  if (!field) {
    return false;
  }
  const name = field.trim();
  return /^(.*[_-])?timestamps?$/i.test(name) || /ts$/i.test(name);
}

export function isIgnorableSyncCompareField(field?: string | null): boolean {
  if (!field) {
    return false;
  }
  const leaf = field.trim().split(".").pop()?.replace(/\[\d+\]/g, "") || "";
  return IGNORED_SYNC_COMPARE_FIELDS.has(leaf);
}

export function omitIgnoredSyncCompareFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(omitIgnoredSyncCompareFields);
  }
  if (value && typeof value === "object") {
    const next: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      if (!IGNORED_SYNC_COMPARE_FIELDS.has(key)) {
        next[key] = omitIgnoredSyncCompareFields(child);
      }
    });
    return next;
  }
  return value;
}

export function compareStatusIgnoringTimestamps(compare?: EntityCompare | null): "pass" | "fail" | "" {
  if (!compare) {
    return "";
  }

  const hasMissing =
    (compare.missingInCloud || []).length > 0 ||
    (compare.missingOnDevice || []).length > 0;
  const hasFieldDiff = (compare.differences || []).some((diff) =>
    (diff.fields || []).some((field) => !isIgnorableSyncCompareField(field.field))
  );

  return hasMissing || hasFieldDiff ? "fail" : "pass";
}

function valueHasItems(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value !== "object") {
    return true;
  }
  const dataKeys = Object.keys(value as Record<string, unknown>).filter(
    (key) => !isIgnorableTimestampField(key)
  );
  return dataKeys.some((key) => valueHasItems((value as Record<string, unknown>)[key]));
}

export function snapshotHasData(snapshot: unknown): boolean {
  if (snapshot == null) {
    return false;
  }
  const root = snapshot as Record<string, unknown>;
  return valueHasItems(root.data !== undefined ? root.data : snapshot);
}

export function canCompareEntity(result?: {
  error?: unknown;
  device?: unknown;
  cloud?: unknown;
  compare?: EntityCompare | null;
  subEntities?: Record<string, EntityCompare | null | undefined>;
} | null): boolean {
  if (!result || result.error) {
    return false;
  }
  if (result.device === null || result.cloud === null) {
    return false;
  }
  return true;
}

export function entityStatusIgnoringTimestamps(result?: {
  error?: unknown;
  device?: unknown;
  cloud?: unknown;
  compare?: EntityCompare | null;
  subEntities?: Record<string, EntityCompare | null | undefined>;
} | null): "pass" | "fail" | "" {
  if (!canCompareEntity(result)) {
    return "";
  }
  if (result?.device != null && result?.cloud != null && !isEqualJson(result.device, result.cloud)) {
    return "fail";
  }
  if (result?.subEntities) {
    const failed = Object.values(result.subEntities).some(
      (sub) => compareStatusIgnoringTimestamps(sub) === "fail"
    );
    return failed ? "fail" : "pass";
  }
  return compareStatusIgnoringTimestamps(result?.compare) || "pass";
}

function compareHasIds(compare: EntityCompare | null | undefined, key: "missingInCloud" | "missingOnDevice"): boolean {
  return (compare?.[key] || []).length > 0;
}

function entityHasMissing(
  result: {
    compare?: EntityCompare | null;
    subEntities?: Record<string, EntityCompare | null | undefined>;
  } | null | undefined,
  key: "missingInCloud" | "missingOnDevice"
): boolean {
  if (!result) {
    return false;
  }
  if (result.subEntities) {
    return Object.values(result.subEntities).some((sub) => compareHasIds(sub, key));
  }
  return compareHasIds(result.compare, key);
}

export function entityHasMissingInCloud(result?: {
  device?: unknown;
  cloud?: unknown;
  compare?: EntityCompare | null;
  subEntities?: Record<string, EntityCompare | null | undefined>;
} | null): boolean {
  if (
    result?.device != null &&
    result?.cloud != null &&
    snapshotHasData(result.device) &&
    !snapshotHasData(result.cloud)
  ) {
    return true;
  }
  return entityHasMissing(result, "missingInCloud");
}

export function entityHasMissingOnDevice(result?: {
  device?: unknown;
  cloud?: unknown;
  compare?: EntityCompare | null;
  subEntities?: Record<string, EntityCompare | null | undefined>;
} | null): boolean {
  if (
    result?.device != null &&
    result?.cloud != null &&
    snapshotHasData(result.cloud) &&
    !snapshotHasData(result.device)
  ) {
    return true;
  }
  return entityHasMissing(result, "missingOnDevice");
}

export function presenceForMissingKind(
  kind: DiffRow["kind"]
): { devicePresent: boolean; cloudPresent: boolean } | null {
  if (kind === "missingOnDevice") {
    return { devicePresent: false, cloudPresent: true };
  }
  if (kind === "missingInCloud") {
    return { devicePresent: true, cloudPresent: false };
  }
  return null;
}

export type EntityFetchState = "loading" | "fetched" | "missing";

export function entitySideFetchState(
  result: { device?: unknown; cloud?: unknown } | null | undefined,
  side: "device" | "cloud"
): EntityFetchState {
  if (!result) {
    return "loading";
  }
  return result[side] != null ? "fetched" : "missing";
}

export function entityMissingPresence(result?: {
  compare?: EntityCompare | null;
  subEntities?: Record<string, EntityCompare | null | undefined>;
} | null): { devicePresent: boolean; cloudPresent: boolean } | null {
  const missingOnDevice = entityHasMissingOnDevice(result);
  const missingInCloud = entityHasMissingInCloud(result);
  if (!missingOnDevice && !missingInCloud) {
    return null;
  }
  return {
    devicePresent: !missingOnDevice,
    cloudPresent: !missingInCloud
  };
}

export function overallStatusIgnoringTimestamps(
  entities: Record<string, { error?: unknown; device?: unknown; cloud?: unknown; compare?: EntityCompare | null; subEntities?: Record<string, EntityCompare | null | undefined> }>
): "pass" | "fail" | "" {
  const comparable = Object.values(entities).filter((result) => canCompareEntity(result));
  if (comparable.length === 0) {
    return "";
  }
  return comparable.some((result) => entityStatusIgnoringTimestamps(result) === "fail")
    ? "fail"
    : "pass";
}

export function toPrettyJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return String(value);
  }
}

const SNAPSHOT_COLLECTION_KEYS = [
  "bacnetDevices",
  "bacnetUnits",
  "bacnetTemplates",
  "modbusDevices",
  "modbusUnits",
  "devices",
  "units",
  "templates",
  "param",
  "ui",
  "schedules",
  "interlockRules",
  "changeoverRules",
  "dualSetpointRules",
  "autoTimerRules"
];

function asItemList(value: unknown): Record<string, unknown>[] {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === "object") as Record<string, unknown>[];
  }
  if (typeof value !== "object") {
    return [];
  }
  const obj = value as Record<string, unknown>;
  for (const key of SNAPSHOT_COLLECTION_KEYS) {
    if (obj[key] != null) {
      return asItemList(obj[key]);
    }
  }
  const values = Object.values(obj);
  if (values.length > 0 && values.every((item) => item && typeof item === "object")) {
    return values as Record<string, unknown>[];
  }
  return [];
}

export function collectSnapshotIds(snapshot: unknown): string[] {
  if (snapshot == null) {
    return [];
  }
  const root = snapshot as Record<string, unknown>;
  const data = root.data !== undefined ? root.data : snapshot;
  const ids = asItemList(data)
    .map((item) => {
      const id = item.id ?? item._id ?? item.syncId ?? item.sync_id;
      return id == null ? "" : String(id);
    })
    .filter(Boolean);
  return Array.from(new Set(ids));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function joinJsonPath(path: string, key: string): string {
  return path ? `${path}.${key}` : key;
}

function addIdentityDiffs(
  deviceValue: unknown,
  cloudValue: unknown,
  path: string,
  addRow: (row: DiffRow) => void
) {
  if (isEqualJson(deviceValue, cloudValue)) {
    return;
  }

  if (Array.isArray(deviceValue) || Array.isArray(cloudValue)) {
    if (!Array.isArray(deviceValue) || !Array.isArray(cloudValue)) {
      addRow({
        id: "",
        kind: "changed",
        field: path || "value",
        device: deviceValue,
        cloud: cloudValue
      });
      return;
    }
    if (deviceValue.length !== cloudValue.length) {
      addRow({
        id: "",
        kind: "changed",
        field: path ? `${path} length` : "length",
        device: deviceValue.length,
        cloud: cloudValue.length
      });
    }
    const length = Math.max(deviceValue.length, cloudValue.length);
    for (let index = 0; index < length; index += 1) {
      addIdentityDiffs(deviceValue[index], cloudValue[index], `${path}[${index}]`, addRow);
    }
    return;
  }

  if (isPlainObject(deviceValue) && isPlainObject(cloudValue)) {
    const deviceKeys = Object.keys(deviceValue).filter((key) => !IGNORED_SYNC_COMPARE_FIELDS.has(key));
    const cloudKeys = Object.keys(cloudValue).filter((key) => !IGNORED_SYNC_COMPARE_FIELDS.has(key));
    const cloudKeySet = new Set(cloudKeys);
    const deviceKeySet = new Set(deviceKeys);

    deviceKeys.forEach((key) => {
      if (!cloudKeySet.has(key)) {
        addRow({
          id: "",
          kind: "changed",
          field: joinJsonPath(path, key),
          device: deviceValue[key],
          cloud: undefined
        });
      }
    });
    cloudKeys.forEach((key) => {
      if (!deviceKeySet.has(key)) {
        addRow({
          id: "",
          kind: "changed",
          field: joinJsonPath(path, key),
          device: undefined,
          cloud: cloudValue[key]
        });
      }
    });

    const sameKeys =
      deviceKeys.length === cloudKeys.length && deviceKeys.every((key) => cloudKeySet.has(key));
    if (sameKeys && deviceKeys.some((key, index) => key !== cloudKeys[index])) {
      addRow({
        id: "",
        kind: "changed",
        field: path ? `${path} key order` : "key order",
        device: deviceKeys,
        cloud: cloudKeys
      });
    }

    deviceKeys.forEach((key) => {
      if (cloudKeySet.has(key)) {
        addIdentityDiffs(deviceValue[key], cloudValue[key], joinJsonPath(path, key), addRow);
      }
    });
    return;
  }

  addRow({
    id: "",
    kind: "changed",
    field: path || "value",
    device: deviceValue,
    cloud: cloudValue
  });
}

export function flattenCompareRows(
  compare?: EntityCompare | null,
  deviceSnapshot?: unknown,
  cloudSnapshot?: unknown
): DiffRow[] {
  const rows: DiffRow[] = [];
  const listed = new Set<string>();

  const addRow = (row: DiffRow) => {
    const key = `${row.kind}:${row.id}:${row.field || ""}`;
    if (listed.has(key)) {
      return;
    }
    listed.add(key);
    rows.push(row);
  };

  (compare?.missingOnDevice || []).forEach((id) => {
    addRow({ id, kind: "missingOnDevice" });
  });
  (compare?.missingInCloud || []).forEach((id) => {
    addRow({ id, kind: "missingInCloud" });
  });
  (compare?.differences || []).forEach((diff) => {
    (diff.fields || []).forEach((field) => {
      if (isIgnorableSyncCompareField(field.field)) {
        return;
      }
      addRow({
        id: diff.id,
        kind: "changed",
        field: field.field,
        device: field.device,
        cloud: field.cloud
      });
    });
  });

  const deviceIds = collectSnapshotIds(deviceSnapshot);
  const cloudIds = collectSnapshotIds(cloudSnapshot);
  const cloudIdSet = new Set(cloudIds);
  const deviceIdSet = new Set(deviceIds);

  deviceIds.forEach((id) => {
    if (!cloudIdSet.has(id)) {
      addRow({ id, kind: "missingInCloud" });
    }
  });
  cloudIds.forEach((id) => {
    if (!deviceIdSet.has(id)) {
      addRow({ id, kind: "missingOnDevice" });
    }
  });

  if (
    rows.length === 0 &&
    deviceSnapshot != null &&
    cloudSnapshot != null &&
    snapshotHasData(deviceSnapshot) !== snapshotHasData(cloudSnapshot)
  ) {
    addRow(
      snapshotHasData(deviceSnapshot)
        ? { id: "device data", kind: "missingInCloud" }
        : { id: "cloud data", kind: "missingOnDevice" }
    );
  }

  if (deviceSnapshot != null && cloudSnapshot != null && !isEqualJson(deviceSnapshot, cloudSnapshot)) {
    addIdentityDiffs(deviceSnapshot, cloudSnapshot, "", addRow);
  }

  if (
    rows.length === 0 &&
    deviceSnapshot != null &&
    cloudSnapshot != null &&
    !isEqualJson(deviceSnapshot, cloudSnapshot)
  ) {
    addRow({
      id: "",
      kind: "changed",
      field: "json",
      device: deviceSnapshot,
      cloud: cloudSnapshot
    });
  }

  return rows;
}

export function formatJsonValue(value: unknown): string {
  if (value === undefined) {
    return "—";
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function formatSyncTimestamp(value: unknown): string {
  if (value == null || value === "" || value === 0 || value === "0") {
    return "—";
  }
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) {
    return Number.isFinite(numeric) ? "—" : String(value);
  }
  const date = new Date(numeric);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toISOString();
}

export function formatDisconnectDate(value: unknown): string {
  if (value == null || value === "" || value === 0 || value === "0") {
    return "";
  }
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  const date = new Date(numeric);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString();
}

export function isEqualJson(value1: unknown, value2: unknown): boolean {
  return toPrettyJson(omitIgnoredSyncCompareFields(value1))
    === toPrettyJson(omitIgnoredSyncCompareFields(value2));
}

export function readJsonChild(value: unknown, key: string): unknown {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value[Number(key)];
  }
  return (value as Record<string, unknown>)[key];
}

export function collectJsonKeys(deviceValue: unknown, cloudValue: unknown): string[] {
  if (Array.isArray(deviceValue) || Array.isArray(cloudValue)) {
    const length = Math.max(
      Array.isArray(deviceValue) ? deviceValue.length : 0,
      Array.isArray(cloudValue) ? cloudValue.length : 0
    );
    return Array.from({ length }, (_, index) => String(index));
  }

  const keys = new Set<string>();
  if (deviceValue && typeof deviceValue === "object") {
    Object.keys(deviceValue as Record<string, unknown>).forEach((key) => keys.add(key));
  }
  if (cloudValue && typeof cloudValue === "object") {
    Object.keys(cloudValue as Record<string, unknown>).forEach((key) => keys.add(key));
  }
  return Array.from(keys).sort();
}

export function jsonNodeState(deviceValue: unknown, cloudValue: unknown): "same" | "changed" | "deviceOnly" | "cloudOnly" {
  const deviceMissing = deviceValue === undefined;
  const cloudMissing = cloudValue === undefined;
  if (deviceMissing && !cloudMissing) {
    return "cloudOnly";
  }
  if (!deviceMissing && cloudMissing) {
    return "deviceOnly";
  }
  return isEqualJson(deviceValue, cloudValue) ? "same" : "changed";
}

export function resolveEntityStatus(result: any): "pending" | "unavailable" | "pass" | "fail" {
  if (!result) {
    return "pending";
  }
  if (!canCompareEntity(result)) {
    return "unavailable";
  }
  return entityStatusIgnoringTimestamps(result) === "fail" ? "fail" : "pass";
}
